import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { EvaluationProtocolElementDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from '../../hooks/useScenarioForm';
import type { TransformedElement, TransformedProtocol } from '@/app/queries/evaluationProtocolQueries';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';

import ProtocolListItem from './ProtocolListItem';
import CategorySection from './CategorySection';

type EvaluationFieldName = 'evaluationProtocolElements' | 'notesEvaluationProtocolElements';

type EvaluationProtocolSelectionDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  protocols: TransformedProtocol[];
  fieldName: EvaluationFieldName;
};

const EvaluationProtocolSelectionDialog: React.FC<EvaluationProtocolSelectionDialogProps> = ({
  open,
  onClose,
  title,
  description,
  protocols,
  fieldName
}) => {
  const { watch, setValue, getValues } = useFormContext<ScenarioSettingsType>();
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);

  const selectedElements = watch(fieldName) || [];

  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [showOnlyUsed, setShowOnlyUsed] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState<number | null>(null);

  const selectedElementsSet = useMemo(() => new Set(selectedElements.map((el) => el.id)), [selectedElements]);

  const protocolSelectionData = useMemo(() => {
    const dataMap = new Map<number, { selectedCount: number; totalCount: number }>();
    protocols.forEach((protocol) => {
      const totalCount = protocol.categories?.reduce((total, category) => total + (category.elements?.length || 0), 0) || 0;
      const selectedCount =
        protocol.categories?.reduce(
          (total, category) =>
            total + (category.elements?.filter((element) => selectedElementsSet.has(element.id)).length || 0),
          0
        ) || 0;
      dataMap.set(protocol.id, { selectedCount, totalCount });
    });
    return dataMap;
  }, [protocols, selectedElementsSet]);

  const filteredAndSortedProtocols = useMemo(() => {
    let filtered = protocols;

    if (showOnlyUsed) {
      filtered = filtered.filter((protocol) => {
        const data = protocolSelectionData.get(protocol.id);
        return data && data.selectedCount > 0;
      });
    }

    if (categorySearchTerm) {
      const searchLower = categorySearchTerm.toLowerCase();
      filtered = filtered.filter((protocol) => {
        const categories = protocol.categories || [];
        return categories.some((category) => category.title.toLowerCase().includes(searchLower));
      });
    }

    return [...filtered].sort((a, b) => a.title.localeCompare(b.title));
  }, [protocols, showOnlyUsed, protocolSelectionData, categorySearchTerm]);

  const selectedProtocol = useMemo(() => {
    if (!selectedProtocolId) return null;
    return protocols.find((p) => p.id === selectedProtocolId) || null;
  }, [selectedProtocolId, protocols]);

  const filteredCategories = useMemo(() => {
    if (!selectedProtocol) return [];

    const searchLower = categorySearchTerm.toLowerCase();
    let categories = selectedProtocol.categories || [];

    if (categorySearchTerm) {
      categories = categories.filter((category) => category.title.toLowerCase().includes(searchLower));
    }

    if (showOnlyUsed) {
      categories = categories
        .map((category) => ({
          ...category,
          elements: category.elements?.filter((element) => selectedElementsSet.has(element.id)) || []
        }))
        .filter((category) => category.elements.length > 0);
    }

    return categories;
  }, [selectedProtocol, categorySearchTerm, showOnlyUsed, selectedElementsSet]);

  const protocolStats = useMemo(() => {
    let withSelections = 0;
    protocolSelectionData.forEach((data) => {
      if (data.selectedCount > 0) withSelections++;
    });

    return {
      total: protocols.length,
      withSelections,
      totalSelected: selectedElements.length
    };
  }, [protocols, selectedElements, protocolSelectionData]);

  const handleClearAllSelections = useCallback(() => {
    setValue(fieldName, [], { shouldDirty: true });
  }, [fieldName, setValue]);

  const handleProtocolSelect = useCallback((protocolId: number) => {
    setSelectedProtocolId(protocolId);
  }, []);

  const handleCategorySelectAllToggle = useCallback(
    (categoryElements: TransformedElement[], isCurrentlyAllSelected: boolean) => {
      const currentElements = getValues(fieldName) || [];

      let newElements: EvaluationProtocolElementDto[];

      if (isCurrentlyAllSelected) {
        newElements = currentElements.filter(
          (selectedEl) => !categoryElements.some((catEl) => catEl.id === selectedEl.id)
        );
      } else {
        const elementsToAdd = categoryElements
          .filter((catEl) => !currentElements.some((selectedEl) => selectedEl.id === catEl.id))
          .map((element) => ({
            id: element.id,
            description: element.description,
            order: element.order,
            type: element.type,
            choices: element.choices,
            scaleMin: element.scaleMin,
            scaleMax: element.scaleMax,
            explanationEnabled: element.explanationEnabled,
            evaluationProtocolCategoryId: element.categoryId
          }));

        newElements = [...currentElements, ...elementsToAdd];
      }

      setValue(fieldName, newElements, { shouldDirty: true });
    },
    [fieldName, getValues, setValue]
  );

  const handleElementToggle = useCallback(
    (element: TransformedElement) => {
      const currentElements = getValues(fieldName) || [];
      const elementIndex = currentElements.findIndex((el) => el.id === element.id);

      const transformedElementDto: EvaluationProtocolElementDto = {
        id: element.id,
        description: element.description,
        order: element.order,
        type: element.type,
        choices: element.choices,
        scaleMin: element.scaleMin,
        scaleMax: element.scaleMax,
        explanationEnabled: element.explanationEnabled,
        evaluationProtocolCategoryId: element.categoryId
      };

      if (elementIndex === -1) {
        setValue(fieldName, [...currentElements, transformedElementDto], { shouldDirty: true });
      } else {
        const newElements = [...currentElements];
        newElements.splice(elementIndex, 1);
        setValue(fieldName, newElements, { shouldDirty: true });
      }
    },
    [fieldName, getValues, setValue]
  );

  const handleCategorySearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCategorySearchTerm(e.target.value);
  }, []);

  const handleToggleShowOnlyUsed = useCallback(() => {
    setShowOnlyUsed((prev) => !prev);
  }, []);

  useEffect(() => {
    if (protocols.length > 0 && selectedProtocolId === null) {
      const firstWithSelections = filteredAndSortedProtocols.find((p) => {
        const data = protocolSelectionData.get(p.id);
        return data && data.selectedCount > 0;
      });
      setSelectedProtocolId(firstWithSelections?.id || filteredAndSortedProtocols[0]?.id || null);
    }
  }, [protocols, selectedProtocolId, filteredAndSortedProtocols, protocolSelectionData]);

  useEffect(() => {
    if (open) {
      setCategorySearchTerm('');
      setShowOnlyUsed(false);
      setSelectedProtocolId(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ minHeight: 560 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocols))}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.searchCategories))}
              value={categorySearchTerm}
              onChange={handleCategorySearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: categorySearchTerm && (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => setCategorySearchTerm('')} sx={{ minWidth: 'auto', p: 0.5 }}>
                      <ClearIcon fontSize="small" />
                    </Button>
                  </InputAdornment>
                )
              }}
              sx={{ width: 220 }}
            />
            <Chip
              label={`${protocolStats.totalSelected} ${translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.selected))}`}
              color={protocolStats.totalSelected > 0 ? 'primary' : 'default'}
              size="small"
            />
            <Chip
              label={`${protocolStats.withSelections}/${protocolStats.total} ${translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.protocolsUsed))}`}
              variant="outlined"
              size="small"
            />
            <Button
              variant={showOnlyUsed ? 'contained' : 'outlined'}
              size="small"
              startIcon={<FilterListIcon />}
              onClick={handleToggleShowOnlyUsed}
            >
              {showOnlyUsed
                ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.showAll))
                : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.showUsedOnly))}
            </Button>
            {protocolStats.totalSelected > 0 && (
              <Button variant="outlined" size="small" color="error" startIcon={<ClearIcon />} onClick={handleClearAllSelections}>
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.clearAll))}
              </Button>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'flex',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            height: 480
          }}
        >
          <Box
            sx={{
              width: 320,
              minWidth: 280,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper'
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.protocols))}
              </Typography>
            </Box>

            <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
              {filteredAndSortedProtocols.map((protocol) => {
                const selectionData = protocolSelectionData.get(protocol.id);
                return (
                  <ProtocolListItem
                    key={protocol.id}
                    protocol={protocol}
                    isSelected={protocol.id === selectedProtocolId}
                    selectedCount={selectionData?.selectedCount || 0}
                    totalCount={selectionData?.totalCount || 0}
                    onClick={handleProtocolSelect}
                  />
                );
              })}
            </List>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {selectedProtocol ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {selectedProtocol.title}
                </Typography>

                {filteredCategories.map((category, index) => (
                  <CategorySection
                    key={category.id}
                    categoryId={category.id}
                    title={category.title}
                    elements={category.elements}
                    selectedElementsSet={selectedElementsSet}
                    onCategorySelectAllToggle={handleCategorySelectAllToggle}
                    onElementToggle={handleElementToggle}
                    showDivider={index !== filteredCategories.length - 1}
                  />
                ))}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.noProtocolsFound))}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.close))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvaluationProtocolSelectionDialog;
